import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { CheckCircle2, Plus, Search, Shield, UserPlus } from 'lucide-react'
import { Link } from '@/lib/router'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip, ChipGroup } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'

import { num, relativeTime } from '@/lib/format'
import { useLabel } from '@/lib/labels'

import {
  useCreateUser,
  useGrantRole,
  useOffboardUser,
  useReactivateUser,
  useRevokeRole,
  useRoles,
  useSetUserStatus,
  useUsers,
  type AdminUser,
  type Role,
  usePaymentModes,
  useSetPaymentModes,
} from './api'
import { useAdminResetPassword } from './passwordApi'
import { UserDetailsEditor } from './UserDetailsEditor'
import { useSetUserTotpRequired, useAdminResetTotp } from './totpApi'
import { useOrgTree, type NodeType } from './orgApi'
import { useTenantSettings } from '../settings/api'
import { useAuthStore } from '@/lib/auth/store'

/** Manager roles whose grant can be pinned to an org node (BUG-001/007) —
 *  the pin scopes their case/allocation/user visibility to that subtree. */
const SCOPED_ROLE_NODE_TYPE: Record<string, NodeType> = {
  TEAM_LEAD: 'TEAM',
  BRANCH_MANAGER: 'BRANCH',
  AREA_MANAGER: 'AREA',
  REGIONAL_MANAGER: 'REGION',
}

/**
 * Tenant user + role admin. Routes mounted under /admin/users.
 *
 * <p>Three flows:
 *   <ul>
 *     <li>List users with their role badges + status + last login.</li>
 *     <li>Add user (modal-ish inline form).</li>
 *     <li>Per-row role manage drawer + status toggle.</li>
 *   </ul>
 * </p>
 */
export function UsersAdminPage() {
  const users = useUsers()
  const roles = useRoles()
  const tenantSettings = useTenantSettings()
  const totpEnforcementEnabled = tenantSettings.data?.security?.manager_totp_required === true
  const [showCreate, setShowCreate] = useState(false)
  const [q, setQ] = useState('')

  // Client-side: the tenant user list is already fetched whole and is a few
  // hundred rows at most. Matches emp id, name and role code, because "who are
  // my telecallers" is the question this page gets asked most.
  const visibleUsers = useMemo(() => {
    const norm = q.trim().toLowerCase()
    if (!norm) return users.data ?? []
    return (users.data ?? []).filter((u) =>
      u.empId.toLowerCase().includes(norm) ||
      (u.fullName ?? '').toLowerCase().includes(norm) ||
      u.roles.some((r) => r.toLowerCase().includes(norm)),
    )
  }, [users.data, q])
  const [editingUser, setEditingUser] = useState<string | null>(null)

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
            Tenant administration
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight text-navy-900">
            <Shield className="h-5 w-5" /> Users &amp; roles
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Add agents and managers. Grant or revoke roles. Suspend accounts
            when an agent leaves. Every change is in the audit chain.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <UserPlus className="h-3.5 w-3.5" />
          Add user
        </Button>
      </div>

      {showCreate && <CreateUserForm onClose={() => setShowCreate(false)} />}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>All users</CardTitle>
              <CardDescription>
                {users.data
                  ? q.trim()
                    ? `${num(visibleUsers.length)} of ${num(users.data.length)} accounts`
                    : `${num(users.data.length)} accounts in this tenant`
                  : '—'}
              </CardDescription>
            </div>
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-navy-500" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Emp ID, name or role…"
                className="h-8 pl-8 text-xs"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {users.isPending && <SkeletonRows rows={5} cols={5} />}
          {users.isError && (
            <div className="px-5 pb-4 text-sm text-negative">
              Couldn't load users — USER_VIEW is required.
            </div>
          )}
          {users.data && visibleUsers.length === 0 && (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium text-navy-900">
                {q.trim() ? 'No matching users' : 'No users yet'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {q.trim()
                  ? 'Search matches Emp ID, name and role.'
                  : 'Click "Add user" to provision the first agent.'}
              </p>
            </div>
          )}
          {users.data && visibleUsers.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
                  <tr>
                    <th className="px-5 py-2 font-medium">Emp ID</th>
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Roles</th>
                    <th className="px-3 py-2 font-medium">TOTP</th>
                    <th className="px-3 py-2 font-medium">Last login</th>
                    <th className="px-5 py-2 text-right font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.map((u) => (
                    <UserRow
                      key={u.userId}
                      u={u}
                      allRoles={roles.data ?? []}
                      isEditing={editingUser === u.userId}
                      onToggle={() => setEditingUser((cur) => (cur === u.userId ? null : u.userId))}
                      totpEnforcementEnabled={totpEnforcementEnabled}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// User row + role editor
// ---------------------------------------------------------------------------

function UserRow({
  u,
  allRoles,
  isEditing,
  onToggle,
  totpEnforcementEnabled,
}: {
  u: AdminUser
  allRoles: Role[]
  isEditing: boolean
  onToggle: () => void
  /** The tenant-wide "Require TOTP for manager logins" toggle (Settings →
   *  Security) — the master switch. The per-user override below is disabled
   *  while this is off; it has no effect on its own. */
  totpEnforcementEnabled: boolean
}) {
  const setStatus = useSetUserStatus()
  const grant = useGrantRole()
  const revoke = useRevokeRole()
  const offboard = useOffboardUser()
  const reactivate = useReactivateUser()
  const resetPassword = useAdminResetPassword()
  const setTotpRequired = useSetUserTotpRequired()
  const resetTotp = useAdminResetTotp()
  const userRoleCodes = new Set(u.roles)
  const isOffboarded = u.status === 'OFFBOARDED'
  const isSelf = useAuthStore((s) => s.empId) === u.empId

  function onResetPassword() {
    if (resetPassword.isPending) return
    const temp = window.prompt(
      `Set a temporary password for ${u.empId} (at least 8 characters).\n\n` +
        `They will be required to change it on their next login. Share it with them out-of-band ` +
        `(WhatsApp, SMS) — Graviton does not deliver it for you.`,
    )
    if (!temp) return
    if (temp.length < 8) {
      window.alert('Temporary password must be at least 8 characters.')
      return
    }
    resetPassword.mutate({ userId: u.userId, tempPassword: temp })
  }

  function onToggleTotpRequired() {
    if (setTotpRequired.isPending) return
    const next = !u.totpRequired
    if (next && !u.totpEnrolled) {
      const ok = window.confirm(
        `${u.empId} hasn't enrolled TOTP yet. Requiring it now will block their next login until ` +
          `they enrol from Profile → Security — which itself requires being logged in. Make sure they ` +
          `know to enrol first. Continue?`,
      )
      if (!ok) return
    }
    setTotpRequired.mutate({ userId: u.userId, totpRequired: next })
  }

  function onResetTotp() {
    if (resetTotp.isPending) return
    const ok = window.confirm(
      `Reset TOTP for ${u.empId}? This clears their authenticator pairing immediately and signs ` +
        `them out of every active session. They'll need to re-enrol from Profile → Security before ` +
        `they can sign in again` +
        (u.totpRequired
          ? " — and TOTP is currently required for this user, so they won't be able to sign back in until they do."
          : '.') +
        (isSelf ? '\n\nThis is your own account — you will be signed out immediately.' : ''),
    )
    if (ok) resetTotp.mutate({ userId: u.userId })
  }

  function totpResetErrorMessage(): string {
    if (isAxiosError(resetTotp.error)) {
      const status = resetTotp.error.response?.status
      if (status === 403) return "Couldn't reset — USER_MANAGE is required."
      if (status === 404) return 'This user has no TOTP enrollment to reset.'
    }
    return "Couldn't reset TOTP. Try again."
  }

  // Granting a manager role pauses on a node picker so the admin can pin the
  // grant to a subtree (BUG-001/007). Skipping the pin keeps the historical
  // tenant-wide behavior until an org anchor exists.
  const [pendingScopedRole, setPendingScopedRole] = useState<string | null>(null)
  const [pendingNodeId, setPendingNodeId] = useState<string>('')
  const { data: orgNodes } = useOrgTree()

  // Payment modes — only fetched once the row is expanded.
  const { data: payModes } = usePaymentModes(isEditing ? u.userId : null)
  const setPayModes = useSetPaymentModes()
  const label = useLabel()

  function togglePaymentMode(mode: string) {
    if (!payModes) return
    // An unrestricted agent shows every chip as active. The first click must
    // therefore mean "restrict to everything EXCEPT this one", not "restrict
    // to only this one" — otherwise a stray click silently strips five modes.
    const current = payModes.unrestricted ? payModes.allModes : payModes.modes
    const next = current.includes(mode)
      ? current.filter((m: string) => m !== mode)
      : [...current, mode]
    // Selecting everything is the same as no restriction; send [] so the row
    // reads as "unrestricted" rather than pinning a list that must be
    // maintained as new modes are added.
    const sameAsAll = next.length === payModes.allModes.length
    setPayModes.mutate({ userId: u.userId, modes: sameAsAll ? [] : next })
  }

  function toggleRole(code: string) {
    if (userRoleCodes.has(code)) {
      revoke.mutate({ userId: u.userId, roleCode: code })
      return
    }
    if (code in SCOPED_ROLE_NODE_TYPE) {
      setPendingScopedRole(code)
      setPendingNodeId('')
      return
    }
    grant.mutate({ userId: u.userId, roleCode: code })
  }

  function confirmScopedGrant() {
    if (!pendingScopedRole) return
    grant.mutate({
      userId: u.userId,
      roleCode: pendingScopedRole,
      ...(pendingNodeId ? { scopeNodeId: pendingNodeId } : {}),
    })
    setPendingScopedRole(null)
    setPendingNodeId('')
  }

  const pendingNodeOptions = pendingScopedRole
    ? (orgNodes ?? []).filter(
        (n) => n.nodeType === SCOPED_ROLE_NODE_TYPE[pendingScopedRole] && n.status === 'ACTIVE',
      )
    : []

  return (
    <>
      <tr className="border-b border-slate-200 last:border-0 hover:bg-navy-50">
        <td className="px-5 py-2 font-mono text-xs text-navy-900">{u.empId}</td>
        <td className="px-3 py-2 text-navy-900">{u.fullName}</td>
        <td className="px-3 py-2">
          <Badge tone={u.status === 'ACTIVE' ? 'positive' : u.status === 'SUSPENDED' ? 'warning' : 'slate'}>
            {u.status}
          </Badge>
        </td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap gap-1">
            {u.roles.length === 0 && <span className="text-2xs text-muted-foreground">No roles</span>}
            {u.roles.map((r) => (
              <Badge key={r} tone="navy">{r}</Badge>
            ))}
          </div>
        </td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap items-center gap-1">
            <Badge tone={u.totpEnrolled ? 'positive' : 'slate'}>
              {u.totpEnrolled ? 'Enrolled' : 'Not enrolled'}
            </Badge>
            {u.totpRequired && <Badge tone="info">Required</Badge>}
          </div>
        </td>
        <td className="px-3 py-2 text-muted-foreground">
          {u.lastLoginAt ? relativeTime(u.lastLoginAt) : '—'}
        </td>
        <td className="px-5 py-2 text-right">
          <Button variant="outline" size="sm" onClick={onToggle}>
            {isEditing ? 'Close' : 'Manage'}
          </Button>
        </td>
      </tr>
      {isEditing && (
        <tr className="border-b border-slate-200 bg-navy-50/30">
          <td colSpan={7} className="px-5 py-3">
            <div className="mb-4 border-b border-slate-200 pb-4">
              <p className="mb-2 text-2xs font-semibold uppercase tracking-wider text-navy-700">
                Details
              </p>
              <UserDetailsEditor userId={u.userId} disabled={isOffboarded} />
            </div>
            <div className="grid gap-4 sm:grid-cols-[2fr_1fr_1fr]">
              <div>
                <p className="mb-2 text-2xs font-semibold uppercase tracking-wider text-navy-700">
                  Roles
                </p>
                <ChipGroup>
                  {allRoles.map((r) => (
                    <Chip
                      key={r.code}
                      active={userRoleCodes.has(r.code)}
                      onClick={() => toggleRole(r.code)}
                    >
                      {r.code}
                    </Chip>
                  ))}
                </ChipGroup>
                {pendingScopedRole && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-navy-200 bg-white p-2.5">
                    <span className="text-2xs font-semibold uppercase tracking-wider text-navy-700">
                      Scope {pendingScopedRole} to
                    </span>
                    <select
                      value={pendingNodeId}
                      onChange={(e) => setPendingNodeId(e.target.value)}
                      className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-navy-900"
                    >
                      <option value="">Tenant-wide (no pin)</option>
                      {pendingNodeOptions.map((n) => (
                        <option key={n.nodeId} value={n.nodeId}>
                          {n.code} — {n.name}
                        </option>
                      ))}
                    </select>
                    <Button size="sm" onClick={confirmScopedGrant} disabled={grant.isPending}>
                      Grant
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setPendingScopedRole(null)}>
                      Cancel
                    </Button>
                    {pendingNodeOptions.length === 0 && (
                      <span className="text-2xs text-muted-foreground">
                        No {SCOPED_ROLE_NODE_TYPE[pendingScopedRole]} nodes yet — create them in Org Structure.
                      </span>
                    )}
                  </div>
                )}
                <p className="mt-2 text-2xs text-muted-foreground">
                  Click to grant or revoke. Manager roles can be pinned to an org node —
                  the pin bounds what they see. Every change is logged.
                </p>

                <p className="mb-2 mt-4 text-2xs font-semibold uppercase tracking-wider text-navy-700">
                  Payment modes
                </p>
                {!payModes && (
                  <p className="text-2xs text-muted-foreground">Loading…</p>
                )}
                {payModes && (
                  <>
                    <ChipGroup>
                      {payModes.allModes.map((m: string) => (
                        <Chip
                          key={m}
                          active={payModes.unrestricted || payModes.modes.includes(m)}
                          onClick={() => togglePaymentMode(m)}
                        >
                          {label('paymentMode', m)}
                        </Chip>
                      ))}
                    </ChipGroup>
                    <p className="mt-2 text-2xs text-muted-foreground">
                      {payModes.unrestricted
                        ? 'No restriction — this agent may collect in any mode. Deselect one to restrict.'
                        : `Restricted to ${payModes.modes.map((m: string) => label('paymentMode', m)).join(', ') || 'nothing selected'}. ` +
                          'Selecting all clears the restriction. Enforced on the server, not just in the app.'}
                    </p>
                  </>
                )}
              </div>
              <div>
                <p className="mb-2 text-2xs font-semibold uppercase tracking-wider text-navy-700">
                  Status
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={u.status === 'ACTIVE' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatus.mutate({ userId: u.userId, status: 'ACTIVE' })}
                    disabled={u.status === 'ACTIVE' || setStatus.isPending || isOffboarded}
                  >
                    Activate
                  </Button>
                  <Button
                    variant={u.status === 'SUSPENDED' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatus.mutate({ userId: u.userId, status: 'SUSPENDED' })}
                    disabled={u.status === 'SUSPENDED' || setStatus.isPending || isOffboarded}
                  >
                    Suspend
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onResetPassword}
                    disabled={isOffboarded || resetPassword.isPending}
                  >
                    {resetPassword.isPending ? 'Resetting…' : 'Reset password'}
                  </Button>
                  {isOffboarded ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        if (reactivate.isPending) return
                        const ok = window.confirm(
                          `Reactivate ${u.empId}? They can sign in again with the same Emp ID and roles. ` +
                            `Allocations revoked when they were offboarded are not restored — allocate their cases again.`,
                        )
                        if (ok) reactivate.mutate({ userId: u.userId })
                      }}
                      disabled={reactivate.isPending}
                    >
                      {reactivate.isPending ? 'Reactivating…' : 'Reactivate'}
                    </Button>
                  ) : (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        if (offboard.isPending) return
                        const ok = window.confirm(
                          `Offboard ${u.empId}? This revokes every ACTIVE allocation they hold and freezes the account. ` +
                            `You can reactivate them later, but the allocations are not restored.`,
                        )
                        if (ok) offboard.mutate({ userId: u.userId })
                      }}
                      disabled={offboard.isPending}
                    >
                      {offboard.isPending ? 'Offboarding…' : 'Offboard'}
                    </Button>
                  )}
                </div>
                {resetPassword.isSuccess && (
                  <p className="mt-2 text-2xs text-positive">
                    Temporary password set · they'll be forced to change it next login.
                  </p>
                )}
                {resetPassword.isError && (
                  <p className="mt-2 text-2xs text-negative">
                    Couldn't reset — USER_MANAGE is required.
                  </p>
                )}
                {offboard.isSuccess && isOffboarded && (
                  <p className="mt-2 text-2xs text-positive">
                    Offboarded · {offboard.data?.revokedAllocations ?? 0} allocations revoked.
                  </p>
                )}
                {reactivate.isSuccess && !isOffboarded && (
                  <p className="mt-2 text-2xs text-positive">
                    Reactivated · they can sign in again. Allocate their cases afresh.
                  </p>
                )}
                {reactivate.isError && (
                  <p className="mt-2 text-2xs text-negative">
                    {isAxiosError(reactivate.error) && reactivate.error.response?.status === 403
                      ? "Couldn't reactivate — USER_MANAGE is required."
                      : "Couldn't reactivate. Refresh and try again."}
                  </p>
                )}
                <p className="mt-2 text-2xs text-muted-foreground">
                  Suspended users can't log in. Offboarding additionally
                  revokes every ACTIVE allocation and emits USER.OFFBOARDED
                  to the audit chain; reactivating brings the user back but
                  not those allocations.
                </p>
              </div>
              <div>
                <p className="mb-2 text-2xs font-semibold uppercase tracking-wider text-navy-700">
                  MFA
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={u.totpRequired ? 'default' : 'outline'}
                    size="sm"
                    onClick={onToggleTotpRequired}
                    disabled={isOffboarded || setTotpRequired.isPending || !totpEnforcementEnabled}
                  >
                    {u.totpRequired ? 'Required (per-user)' : 'Require TOTP'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onResetTotp}
                    disabled={isOffboarded || resetTotp.isPending || !u.totpEnrolled}
                  >
                    {resetTotp.isPending ? 'Resetting…' : 'Reset TOTP'}
                  </Button>
                </div>
                {setTotpRequired.isSuccess && (
                  <p className="mt-2 text-2xs text-positive">MFA requirement updated.</p>
                )}
                {setTotpRequired.isError && (
                  <p className="mt-2 text-2xs text-negative">
                    Couldn't update — USER_MANAGE is required.
                  </p>
                )}
                {resetTotp.isSuccess && (
                  <p className="mt-2 text-2xs text-positive">
                    TOTP cleared · they'll be forced to re-enrol and sign in again.
                  </p>
                )}
                {resetTotp.isError && (
                  <p className="mt-2 text-2xs text-negative">{totpResetErrorMessage()}</p>
                )}
                <p className="mt-2 text-2xs text-muted-foreground">
                  {totpEnforcementEnabled ? (
                    'Pulls this user into TOTP even if their role wouldn’t otherwise require it.'
                  ) : (
                    <>
                      Only available when{' '}
                      <Link to="/settings" className="underline">
                        "Require TOTP for manager logins"
                      </Link>{' '}
                      is enabled in Settings → Security.
                    </>
                  )}
                </p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Create-user form
// ---------------------------------------------------------------------------

function CreateUserForm({ onClose }: { onClose: () => void }) {
  const create = useCreateUser()
  const [empId, setEmpId] = useState('')
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [password, setPassword] = useState('')

  const canSubmit = empId.length >= 3 && fullName.length >= 2 && password.length >= 8

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    create.mutate(
      {
        empId: empId.trim().toUpperCase(),
        fullName: fullName.trim(),
        email: email.trim() || null,
        mobile: mobile.trim() || null,
        password,
      },
      {
        onSuccess: () => {
          setEmpId(''); setFullName(''); setEmail(''); setMobile(''); setPassword('')
          onClose()
        },
      },
    )
  }

  const errorMsg = create.isError
    ? isAxiosError(create.error) && create.error.response?.status === 403
      ? 'USER_MANAGE is required.'
      : isAxiosError(create.error)
      ? (create.error.response?.data?.detail ?? create.error.response?.data?.message ?? 'Validation failed.')
      : 'Create failed.'
    : null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add user</CardTitle>
        <CardDescription>
          Provision a new agent or manager. Roles can be granted after the
          account is created.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Emp ID</Label>
              <Input
                value={empId}
                onChange={(e) => setEmpId(e.target.value.toUpperCase())}
                placeholder="TELE003"
                pattern="[A-Z0-9]{3,16}"
                required
              />
              <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
                3–16 chars, A–Z, 0–9.
              </p>
            </div>
            <div>
              <Label>Full name</Label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Telecaller Three"
                required
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tele3@example.com"
              />
            </div>
            <div>
              <Label>Mobile</Label>
              <Input
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="+91…"
                pattern="\+[1-9][0-9]{6,14}"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Initial password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 chars"
                required
                minLength={8}
              />
              <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
                Stored as Argon2id. User can change it after first login (Phase 2).
              </p>
            </div>
          </div>

          {errorMsg && (
            <Alert variant="destructive">
              <AlertTitle>Could not create</AlertTitle>
              <AlertDescription>{errorMsg}</AlertDescription>
            </Alert>
          )}
          {create.isSuccess && (
            <Alert variant="positive">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Created</AlertTitle>
              <AlertDescription>
                User added — grant roles below. For Collection Agents,
                also{' '}
                <Link to="/admin/org" className="underline">
                  place them in the org tree
                </Link>{' '}
                under a Branch/Team so visit approval can find a mapped manager.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={!canSubmit || create.isPending}>
              <Plus className="h-3.5 w-3.5" />
              {create.isPending ? 'Creating…' : 'Create user'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
